<template>
  <div class="contact-float">
    <div class="contact-items">
      <div 
        v-for="(item, index) in contactItems" 
        :key="index" 
        class="contact-item"
        @mouseenter="activeItem = index"
        @mouseleave="activeItem = null"
      >
        <i :class="getItemLabel(item)"></i>
        <span class="item-label">{{ item.label }}</span>
        
        <!-- QR Code or Info Popup -->
        <div v-if="activeItem === index" class="info-popup">
          <img :src="fileUrl+item.coverImages" :alt="item.label" class="qr-code">
          <p>{{ item.description }}</p>
        </div>
      </div>
      
      <!-- Divider -->
      <div class="divider"></div>
      
      <!-- Back to Top Button -->
      <div class="contact-item top-btn" @click="scrollToTop">
        <el-icon><Top/></el-icon>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {  onMounted, ref } from 'vue'
import { Top } from '@element-plus/icons-vue'

import { getContactUsFloat } from '@/api'
import { AboutUs } from '@/types/aboutUs'

const fileUrl = import.meta.env.VITE_APP_FILE_URL
const activeItem = ref<number | null>(null)

const getItemLabel = (item: AboutUs) => {
  switch (item.label) {
    case '小程序':
      return 'icon-miniprogram'
    case '电话':
      return 'icon-phone'
    case '邮箱':
      return 'icon-message'
    case '微信':
      return 'icon-weichat'
    case 'QQ':
      return 'icon-qq'
    default:
      return ''
  }
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

const contactItems = ref<AboutUs[]>([])

onMounted( async () => {
  try {
    const response = await getContactUsFloat()
    if (response && response.data) {
      contactItems.value = response.data
    }
  } catch (error) {
    console.error('Error fetching contact items:', error)
  }
})

</script>

<style scoped>
.contact-float {
  position: fixed;
  right: 10px;
  bottom: 120px;
  z-index: 999;
}

.contact-items {
  display: flex;
  flex-direction: column;
  background: rgba(236, 236, 236);
  border-radius: 24px;
  padding: 12px 0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  gap: 8px;
}

.divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  margin: 4px 8px;
}

.contact-item {
  position: relative;
  width: 48px;
  height: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 50%;
  margin: 4px;
  gap: 2px;
}

.contact-item:hover {
  background-color: rgb(230, 237, 255);
}

.contact-item:hover i,
.contact-item:hover .el-icon,
.contact-item:hover .item-label {
  color: rgb(54, 115, 254);
}

.contact-item:hover .icon-miniprogram,
.contact-item:hover .icon-weichat,
.contact-item:hover .icon-phone {
  filter: brightness(0) saturate(100%) invert(37%) sepia(74%) saturate(1303%) hue-rotate(203deg) brightness(99%) contrast(96%);  /* 使SVG变为蓝色 */
}

.contact-item i,
.contact-item .el-icon {
  font-size: 20px;
  color: #565656;
}

.icon-miniprogram, .icon-weichat, .icon-phone {
  display: inline-block;
  width: 26px;
  height: 26px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.icon-miniprogram {
  background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjIiIGhlaWdodD0iMjIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xLjUzNyAxMWE5LjQ2NCA5LjQ2NCAwIDExMTguOTI3LS4wMDEgOS40NjQgOS40NjQgMCAwMS0xOC45MjcgMHpNMTEgMy4xMzRhNy44NjQgNy44NjQgMCAxMDAgMTUuNzI4IDcuODY0IDcuODY0IDAgMDAwLTE1LjcyOHoiIGZpbGw9IiMyMDJENDAiLz48cGF0aCBkPSJNMTMuMTY2IDExQTIuMTY2IDIuMTY2IDAgMTAxMSA4LjgzNHY0LjMzMkEyLjE2NiAyLjE2NiAwIDExOC44MzQgMTEiIHN0cm9rZT0iIzIwMkQ0MCIgc3Ryb2tlLXdpZHRoPSIxLjYiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPjwvc3ZnPg==");
}

.icon-weichat {
  background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjMiIGhlaWdodD0iMjIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTE3LjUyNCAxMC4xOWMtLjA3LTMuNTMtMy4zMjYtNi4zNzQtNy4zMzItNi4zNzQtNC4wNSAwLTcuMzMzIDIuOTA3LTcuMzMzIDYuNDkxIDAgMS42OTIuNzMxIDMuMjMzIDEuOTI5IDQuMzg4bC0uNTUgMi45MjVMNy41IDE2LjM4YzEuNDM2LjQyNCAyLjc5Mi41MyA0LjA2Ny4zMTgiIHN0cm9rZT0iIzIwMkQ0MCIgc3Ryb2tlLXdpZHRoPSIxLjYiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjxwYXRoIGQ9Ik03LjUgOC45MDJjLjU2OSAwIDEuMDMtLjQ2NSAxLjAzLTEuMDM5IDAtLjU3NC0uNDYxLTEuMDM5LTEuMDMtMS4wMzktLjU3IDAtMS4wMzIuNDY1LTEuMDMyIDEuMDQgMCAuNTczLjQ2MiAxLjAzOCAxLjAzMiAxLjAzOHpNMTEuNjI0IDguOTAyYy41NyAwIDEuMDMyLS40NjUgMS4wMzItMS4wMzkgMC0uNTc0LS40NjItMS4wMzktMS4wMzItMS4wMzktLjU2OSAwLTEuMDMuNDY1LTEuMDMgMS4wNCAwIC41NzMuNDYxIDEuMDM4IDEuMDMgMS4wMzh6IiBmaWxsPSIjMjAyRDQwIi8+PHBhdGggZD0iTTE4LjMzMiAxNy42NjhhNS4xMzggNS4xMzggMCAwMS0yLjg3Ljg1N2MtMi42NTcgMC00LjgxMi0xLjk0Ni00LjgxMi00LjM0NiAwLTIuNCAyLjE1NS00LjM0NyA0LjgxMi00LjM0NyAyLjY1OCAwIDQuODEzIDEuOTQ2IDQuODEzIDQuMzQ3IDAgLjY2Mi0uMTY0IDEuMjktLjQ1NyAxLjg1MSIgc3Ryb2tlPSIjMjAyRDQwIiBzdHJva2Utd2lkdGg9IjEuNiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+PHBhdGggZD0iTTE5LjgxOCAxNS43NjJsLjQ1NyAyLjc2Mi0xLjk0Mi0uOTQ5IiBzdHJva2U9IiMyMDJENDAiIHN0cm9rZS13aWR0aD0iMS42IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48cGF0aCBkPSJNMTYuOTIzIDEzLjM5NGEuNzc2Ljc3NiAwIDAxLS43NzMtLjc3OWMwLS40My4zNDYtLjc4Ljc3My0uNzguNDI4IDAgLjc3NC4zNS43NzQuNzggMCAuNDMtLjM0Ni43OC0uNzc0Ljc4ek0xMy44MyAxMy4zOTRhLjc3Ni43NzYgMCAwMS0uNzc0LS43NzljMC0uNDMuMzQ2LS43OC43NzQtLjc4LjQyNyAwIC43NzMuMzUuNzczLjc4IDAgLjQzLS4zNDYuNzgtLjc3My43OHoiIGZpbGw9IiMyMDJENDAiLz48L3N2Zz4=");
}

.icon-phone {
  background-image:url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjMiIGhlaWdodD0iMjIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPjxkZWZzPjxwYXRoIGlkPSJhIiBkPSJNMCAwaDIydjIySDB6Ii8+PC9kZWZzPjxnIHRyYW5zZm9ybT0idHJhbnNsYXRlKC41KSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48bWFzayBpZD0iYiIgZmlsbD0iI2ZmZiI+PHVzZSB4bGluazpocmVmPSIjYSIvPjwvbWFzaz48cGF0aCBkPSJNMTMuNzE2IDIuOTIyYy40NDYgMCAuODA3LjM2Mi44MDcuODA4djMuMjNhLjgwNy44MDcgMCAxMS0xLjYxNSAwVjMuNzNjMC0uNDQ2LjM2Mi0uODA4LjgwOC0uODA4em0zLjIzIDBjLjQ0NiAwIC44MDcuMzYyLjgwNy44MDh2My4yM2EuODA3LjgwNyAwIDExLTEuNjE1IDBWMy43M2MwLS40NDYuMzYyLS44MDguODA4LS44MDh6TTQuODMzIDUuMzQ1YS44MDcuODA3IDAgMDAtLjgwOC43ODcgMTIuMTE5IDEyLjExOSAwIDAwMTEuMzI3IDExLjMyNi44MS44MSAwIDAwLjc4Ni0uODA4di0yLjY4M2wtMi44OS0xLjE1Ny0uODU5IDEuNDI5YS44MDYuODA2IDAgMDEtMS4wNDkuMzA5IDkuNjg3IDkuNjg3IDAgMDEtNC40MDUtNC40MDUuODA2LjgwNiAwIDAxLjMxLTEuMDVsMS40MjgtLjg1Ny0xLjE1Ny0yLjg5MUg0LjgzM3pNMy4xMiA0LjQzOWEyLjQyMiAyLjQyMiAwIDAxMS43MTMtLjcxaDMuMjNjLjMzIDAgLjYyNy4yMDIuNzUuNTA4bDEuNjE1IDQuMDM4YS44MDcuODA3IDAgMDEtLjMzNS45OTJsLTEuMzUuODFhOC4wNyA4LjA3IDAgMDAyLjY2MiAyLjY2NGwuODExLTEuMzUxYS44MDcuODA3IDAgMDEuOTkyLS4zMzVsNC4wMzggMS42MTVjLjMwNi4xMjMuNTA3LjQyLjUwNy43NXYzLjIzYTIuNDIyIDIuNDIyIDAgMDEtMi40MjIgMi40MjNsLS4wNDktLjAwMUExMy43MzIgMTMuNzMyIDAgMDEyLjQxMiA2LjJjLS4wMDItLjAxNi0uMDAyLS4wMzItLjAwMi0uMDUgMC0uNjQxLjI1NS0xLjI1OC43MS0xLjcxMnoiIGZpbGw9IiMyMDJENDAiIHN0eWxlPSJtaXgtYmxlbmQtbW9kZTpub3JtYWwiIG1hc2s9InVybCgjYikiLz48L2c+PC9zdmc+");
}

.top-btn {
  margin-bottom: 0;
}

.top-btn .el-icon {
  font-size: 18px;
}

.item-label {
  font-size: 12px;
  color: #565656;
  text-align: center;
  line-height: 1;
  transition: color 0.3s ease;
}

.info-popup {
  position: absolute;
  right: 68px;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.12);
  white-space: nowrap;
  z-index: 1000;
}

.info-popup::after {
  content: '';
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  border-left: 10px solid white;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
}

.qr-code {
  width: 140px;
  height: 140px;
  max-width: 180px;
  max-height: 180px;
  margin-bottom: 8px;
  border: 1px solid #eee;
  padding: 6px;
  border-radius: 8px;
  background: #fff;
  object-fit: contain;
  display: block;
}

.info-popup p {
  text-align: center;
  color: #565656;
  font-size: 13px;
  margin: 0;
  white-space: nowrap;
}

.info-text {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  padding: 4px 16px;
}
</style>
